import plugin from 'tailwindcss/plugin';

var preset$1 = {
  important: false,
  darkMode: "class",
  theme: {
    /*
    |-----------------------------------------------------------------------------
    | Colors                                  https://tailwindcss.com/docs/colors
    |-----------------------------------------------------------------------------
    |
    | The color palette defined above is also assigned to the "colors" key of
    | your Tailwind config. This makes it easy to access them in your CSS
    | using Tailwind's config helper. For example:
    |
    | .error { color: theme('colors.red') }
    |
    */
    colors: {
      transparent: "transparent",
      primary: "#1E6AC2",
      secondary: "#676f76",
      success: "#36B37E",
      danger: "#FF5630",
      warning: "#FFBC02",
      info: "#6e5aa1",
      body: "#373a40",
      link: "#0867d6",
      muted: "#d0d6da",
      lightest: "#FFFFFF",
      light: "#f3f4f5",
      dark: "#95999a",
      darker: "#2c2c2c",
      darkest: "#101315",
      white: "#FFFFFF",
      "tw-body": "#2F3748",
      "tw-primary": {
        100: "#EEEEEE",
        500: "#5263F1",
        900: "#414FC3"
      },
      "tw-secondary": {
        500: "#676F76",
        800: "#2F3748",
        900: "#1D2432"
      },
      "tw-success": {
        100: "#F2F9F5",
        500: "#36B37E",
        900: "#047857"
      },
      "tw-danger": {
        100: "#FBEFEF",
        500: "#CB3939",
        900: "#991B1B"
      },
      "tw-warning": {
        100: "#FFF5D9",
        500: "#FFBC02"
      },
      "tw-info": {
        100: "#F3EEFF",
        500: "#6E5AA1"
      },
      "tw-neutral": {
        100: "#F3F4F5",
        200: "#D0D6DA",
        500: "#95999A",
        900: "#101315"
      }
    },
    /*
    |-----------------------------------------------------------------------------
    | Screens                       https://tailwindcss.com/docs/responsive-design
    |-----------------------------------------------------------------------------
    |
    | Screens in Tailwind are translated to CSS media queries. They define the
    | responsive breakpoints for your project. By default Tailwind takes a
    | "mobile first" approach, where each screen size represents a minimum
    | viewport width. Feel free to have as few or as many screens as you
    | want, naming them in whatever way you'd prefer for your project.
    |
    | Tailwind also allows for more complex screen definitions, which can be
    | useful in certain situations. Be sure to see the full responsive
    | documentation for a complete list of options.
    |
    | Class name: .{screen}:{utility}
    |
    */

    screens: {
      sm: "640px",
      md: "768px",
      lg: "1024px",
      xl: "1280px"
    },
    spacing: {
      0: "0",
      xs: "0.25rem",
      sm: "0.5rem",
      md: "0.75rem",
      lg: "1rem",
      xl: "1.25rem",
      "2xl": "1.5rem",
      "3xl": "2rem",
      "4xl": "2.5rem",
      "5xl": "3rem",
      "6xl": "4rem"
    },
    fontFamily: {
      sans: ["Lato", "system-ui", "BlinkMacSystemFont", "-apple-system", "Segoe UI", "Roboto", "Oxygen", "Ubuntu", "Cantarell", "Fira Sans", "Droid Sans", "Helvetica Neue", "sans-serif"],
      serif: ["Constantia", "Lucida Bright", "Lucidabright", "Lucida Serif", "Lucida", "DejaVu Serif", "Bitstream Vera Serif", "Liberation Serif", "Georgia", "serif"],
      mono: ["Menlo", "Monaco", "Consolas", "Liberation Mono", "Courier New", "monospace"]
    },
    fontSize: {
      "2xs": ".625rem",
      // 10px
      xs: ".75rem",
      // 12px
      sm: ".875rem",
      // 14px
      base: "1rem",
      // 16px
      lg: "1.125rem",
      // 18px
      xl: "1.25rem",
      // 20px
      "2xl": "1.5rem",
      // 24px
      "3xl": "2rem",
      // 32px
      "4xl": "2.25rem",
      // 36px
      "5xl": "4rem" // 64px
    },

    fontWeight: {
      // 'hairline': 100,
      // 'thin': 200,
      // "light": 300,
      normal: 400,
      // "medium": 500,
      // 'semibold': 600,
      bold: 700,
      // 'extrabold': 800,
      black: 900
    },
    lineHeight: theme => ({
      100: 1,
      125: 1.25,
      150: 1.5,
      160: 1.6,
      200: 2,
      ...theme("fontSize")
    }),
    letterSpacing: {
      tight: "-0.05em",
      normal: "0",
      wide: "0.05em",
      wider: "0.16em",
      "extra-wide": "0.18em",
      widest: "0.24em"
    },
    textColor: theme => theme("colors"),
    backgroundColor: theme => theme("colors"),
    backgroundSize: {
      auto: "auto",
      cover: "cover",
      contain: "contain"
    },
    borderWidth: {
      DEFAULT: "1px",
      0: "0",
      2: "2px",
      4: "4px",
      8: "8px"
    },
    borderColor: theme => theme("colors"),
    /*
    |-----------------------------------------------------------------------------
    | Border radius                    https://tailwindcss.com/docs/border-radius
    |-----------------------------------------------------------------------------
    |
    | Here is where you define your border radius values. If a `DEFAULT` radius
    | is provided, it will be made available as the non-suffixed `.rounded`
    | utility.
    |
    | If your scale includes a `0` value to reset already rounded corners, it's
    | a good idea to put it first so other values are able to override it.
    |
    | Class name: .rounded{-side?}{-size?}
    | CSS property: border-radius
    |
    */

    borderRadius: {
      none: "0",
      base: "0.25rem",
      lg: "0.5rem",
      full: "9999px"
    },
    width: theme => ({
      auto: "auto",
      ...theme("spacing"),
      full: "100%",
      screen: "100vw"
    }),
    height: theme => ({
      auto: "auto",
      px: "1px",
      ...theme("spacing"),
      screen: "100vh",
      full: "100%"
    }),
    minWidth: {
      0: "0",
      xs: "20rem",
      sm: "30rem",
      md: "40rem",
      lg: "50rem",
      xl: "60rem",
      "2xl": "70rem",
      "3xl": "80rem",
      "4xl": "90rem",
      "5xl": "100rem",
      full: "100%"
    },
    minHeight: {
      0: "0",
      sm: "2rem",
      full: "100%",
      screen: "100vh"
    },
    maxWidth: {
      0: "0",
      xs: "20rem",
      sm: "30rem",
      md: "40rem",
      lg: "50rem",
      xl: "60rem",
      "2xl": "70rem",
      "3xl": "80rem",
      "4xl": "90rem",
      "5xl": "100rem",
      full: "100%"
    },
    maxHeight: {
      "4xs": "12rem",
      xs: "20rem",
      full: "100%",
      screen: "100vh"
    },
    padding: theme => theme("spacing"),
    margin: theme => ({
      auto: "auto",
      ...theme("spacing")
    }),
    boxShadow: {
      DEFAULT: "0 4px 4px rgba(0,0,0,0.16)",
      md: "0 6px 4px -4px rgba(0,0,0,0.24)",
      "md:top": "0 -6px 4px -4px rgba(0,0,0,0.24)",
      lg: "0 12px 8px -8px rgba(0,0,0,0.33)",
      "lg:top": "0 -12px 8px -8px rgba(0,0,0,0.33)",
      inner: "inset 0 -2px 4px 0 rgba(0,0,0,0.06)",
      "inner-top": "inset 0 2px 4px 0 rgba(0,0,0,0.06)",
      outline: "0 0 0 3px rgba(52,144,220,0.5)",
      none: "none"
    },
    /* z-index above 100 should not be used inside Hopper components */
    zIndex: {
      auto: "auto",
      0: 0,
      10: 10,
      20: 20,
      30: 30,
      40: 40,
      50: 50,
      100: 100,
      200: 100,
      300: 300
    },
    opacity: {
      0: "0",
      25: ".25",
      50: ".5",
      75: ".75",
      100: "1"
    },
    fill: {
      current: "currentColor"
    },
    stroke: {
      current: "currentColor"
    },
    willChange: {
      opacity: "opacity",
      transform: "transform"
    },
    extend: {
      gridTemplateRows: {
        // Simple 12 row grid
        12: "repeat(12, minmax(0, 1fr))"
      },
      gridRowStart: {
        8: "8",
        9: "9",
        10: "10",
        11: "11",
        12: "12",
        // For consistency
        13: "13"
      },
      gridRowEnd: {
        8: "8",
        9: "9",
        10: "10",
        11: "11",
        12: "12",
        // Until the end of all rows (row 13 does not exist)
        13: "13"
      }
    }
  },
  corePlugins: {
    objectFit: false,
    objectPosition: false
  },
  plugins: [plugin(({
    addVariant
  }) => {
    addVariant("tw", [".tw&", ".tw &"]);
    addVariant("tw-group-optional", [":merge(.group):optional .tw&", ":merge(.group):optional .tw &"]);
    addVariant("tw-peer-optional", [":merge(.peer):optional ~ .tw&", ":merge(.peer):optional ~ .tw &"]);
  })]
};

var preset = preset$1;
var src = {
  preset: preset
};

export { src as default, preset };
//# sourceMappingURL=index.es.js.map
